// @flow
import React from 'react';
import validate from 'validate.js';
import { Form, Message, Input, Button } from 'semantic-ui-react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import Section from 'shared/components/Section';
import LoginStore from 'react/utils/login';
import PreLoginPageTemplate from '../PreLoginPageTemplate';
import LoginEmailField from './LoginEmailField';
import LoginPasswordField from './LoginPasswordField';
import ForgotPasswordLink from './ForgotPasswordLink';
import LoginUtil from './LoginUtil';
import ThemeUtils from 'shared/utils/ThemeUtils';
import UserUtil from 'react/utils/user_util';
import { loadedUser } from 'app/LoggedIn/actions';

class Login extends React.PureComponent {
    state = {
        error: undefined,
        success: undefined,
        email: '',
        password: '',
        isSigningIn: false,
        isSigninSuccessful: false
    };

    emailInputRef = undefined;

    passwordInputRef = undefined;

    componentDidMount() {
        // Defer the focus to wait for animation to finish
        setTimeout(() => {
            focusAndSelect(this.emailInputRef);
        }, 800);
    }

    handleEmailInputChange = (evt, data) => {
        this.setState({ email: data.value });
    };

    handlePasswordInputChange = (evt, data) => {
        this.setState({ password: data.value });
    };

    setPasswordResetEmailSentInfo = () => {
        this.setState({
            success: `An email has been sent to ${this.state.email}. Please check your inbox.`,
            error: undefined
        });
        focusAndSelect(this.passwordInputRef);
    };

    handleSubmit = () => {
        this.setState({ success: undefined });

        const emailError = LoginUtil.getEmailError(this.state.email);
        if (emailError) {
            this.setState({ error: emailError });
            focusAndSelect(this.emailInputRef);
            return;
        }

        const passwordError = getPasswordError(this.state.password);
        if (passwordError) {
            this.setState({ error: passwordError });
            focusAndSelect(this.passwordInputRef);
            return;
        }

        this.setState({ error: undefined, isSigningIn: true });
        LoginStore.login(this.state.email, this.state.password, (errmsg) => {
            if (errmsg) {
                this.setState({ error: 'Wrong username/password combination.', isSigningIn: false });
                focusAndSelect(this.passwordInputRef);
            } else {
                this.setState({ error: undefined, isSigninSuccessful: true }, () => {
                    // Load user data immediately after login success
                    UserUtil.getMiniMe((user) => {
                        LoginStore.setupUser(user);
                        // Dispatch user to Redux store
                        this.props.onLoadUser(user);
                        // Wait for state update and re-render to complete
                        setTimeout(() => {
                            // Use the current URL path and search params, defaulting to /home/realtime if on login page
                            const currentPath = window.location.pathname === '/login' ? '/home/realtime' : window.location.pathname + window.location.search;
                            this.props.history.push(currentPath);
                        }, 1000);
                    });
                });
            }
        });
    };

    render() {
        return (
            <PreLoginPageTemplate hasSessionEndedSuccessfully={this.state.isSigninSuccessful}>
                <Form
                  error={this.state.error} success={this.state.success}
                  onSubmit={this.handleSubmit}
                >
                    <Message error content={this.state.error} />
                    <Message success content={this.state.success} />

                    <LoginEmailField
                        className="login-email-field"
                        email={this.state.email}
                        isSigningIn={this.state.isSigningIn}
                        onChange={this.handleEmailInputChange}
                        inputRef={(inputRef) => { this.emailInputRef = inputRef; }}
                    />
                    <LoginPasswordField
                        className="login-password-field"
                        password={this.state.password}
                        isSigningIn={this.state.isSigningIn}
                        onChange={this.handlePasswordInputChange}
                        inputRef={(inputRef) => { this.passwordInputRef = inputRef; }}
                    />
                    <ForgotPasswordLink
                        email={this.state.email}
                        onEmailInputChange={this.handleEmailInputChange}
                        onPasswordResetEmailSent={this.setPasswordResetEmailSentInfo}
                    />
                    <Section marginTop textAlign="center">
                        <Button
                            primary
                            type="submit"
                            content="Sign In"
                            loading={this.state.isSigningIn}
                            disabled={this.state.isSigningIn}
                            size="large"
                        />
                    </Section>
                </Form>
            </PreLoginPageTemplate>
        );
    }
}

const getPasswordError = (password) => {
    // We do not use the same `getPasswordError` function defined in the
    // `./CommonUtil.js` module because some users might have setup passwords
    // that are less than 8 characters. We won't enforce the 8 character
    // contraint here in order to maintain backward compatibility.
    const constraints = {
        presence: {
            allowEmpty: false,
            message: 'Password cannot be empty.'
        }
    };
    const errors = validate.single(password, constraints);
    return (errors ? errors[0] : undefined);
};

const focusAndSelect = (inputRef) => {
    if (inputRef) {
        inputRef.focus();
        inputRef.select();
    }
};

const mapDispatchToProps = (dispatch) => ({
    onLoadUser: (user) => {
        dispatch(loadedUser(user));
    }
});

export default withRouter(connect(null, mapDispatchToProps)(Login));